import './DocumentChoice.scss'
import {useParams} from "react-router-dom";
import {RouteParam} from "../../types/types";
import {HeaderComponent} from "../../components/common/header/HeaderComponent";
import {ActionCardComponent} from "../../components/common/action-card/ActionCardComponent";
import React, {useCallback, useEffect, useState} from "react";
import {RsDocumentTemplateGroup} from "../../api/dto/RsDocumentTemplateGroup";
import {DocumentTemplateGroupViewModel} from "../../viewmodel/document-choice/DocumentTemplateGroupViewModel";
import {DocumentTemplateViewModel} from "../../viewmodel/document-choice/DocumentTemplateViewModel";
import {useStepCheck} from "../../hooks/stepcheck/useStepCheck";
import {SelectDocumentTemplateViewModel} from "../../viewmodel/document-choice/SelectDocumentTemplateViewModel";
import ProcessingComponent from "../../components/common/processing/ProcessingComponent";
import {ReactComponent as MyMahiIcon} from '../../assets/icons/mymahi.svg';
import {ReactComponent as NoIDIcon} from '../../assets/icons/user-circle.svg';
import {ReactComponent as VerifiedIcon} from '../../assets/icons/verified-id.svg';
import {MyMahiViewModel} from "../../viewmodel/mymahi/MyMahiViewModel";
import {useThemedIcons} from "../../components/common/theme-provider/hooks/useThemedIcons";

export const DocumentChoicePage: React.FC = () => {
    const { verificationId } = useParams<RouteParam>();

    const { evaluateUrl } = useStepCheck(verificationId);
    const { icons } = useThemedIcons()

    const [templateHeader, setTemplateHeader] = useState('')
    const [templateSubheader, setTemplateSubheader] = useState('')

    const {
        loading: documentTemplateGroupLoading,
        documentTemplateGroupList,
        retrieveDocumentTemplateGroupsByVerification
    } = DocumentTemplateGroupViewModel()

    const {
        loading: documentTemplateLoading,
        documentTemplateList,
        retrieveDocumentTemplateByGroup
    } = DocumentTemplateViewModel()

    const {
        selectDocumentTemplate
    } = SelectDocumentTemplateViewModel()

    const {
        loading: myMahiLoading,
        requestVerification,
    } = MyMahiViewModel()

    const onTemplateCardClick = useCallback((chosenDocumentId: string) => {
        if (verificationId) {
            selectDocumentTemplate(verificationId, chosenDocumentId)
        }
    }, [verificationId, selectDocumentTemplate])

    useEffect(() => {
        if (verificationId != null && documentTemplateList != null && documentTemplateList.length === 1) {
            onTemplateCardClick(documentTemplateList[0].id)
        }

    }, [verificationId, documentTemplateList, onTemplateCardClick]);

    const [groupChoiceMade, setGroupChoiceMade] = useState<boolean>(false)

    useEffect(() => {
        const run = async () => {
            await evaluateUrl(); // wait until this finishes
            if (verificationId) {
                await retrieveDocumentTemplateGroupsByVerification(verificationId);
            }
        };

        run();
    }, [evaluateUrl, verificationId, retrieveDocumentTemplateGroupsByVerification])

    const onGroupCardClick = useCallback((group: RsDocumentTemplateGroup) => {
        setGroupChoiceMade(true)
        if (group.name.includes('Student')) {
            setTemplateHeader('Pick your student ID type')
            setTemplateSubheader('Use your student ID to open an Emerge Starter account. You can upgrade later with a verified ID for higher limits and more features.')
        } else {
            setTemplateHeader('Choose your verified ID')
            setTemplateSubheader('Pick the one you have handy, we’ll do the rest.')
        }
        if (verificationId) {
            retrieveDocumentTemplateByGroup(group.id, verificationId)
        }
    }, [retrieveDocumentTemplateByGroup, verificationId])

    useEffect(() => {
        if (verificationId != null && documentTemplateGroupList != null && documentTemplateGroupList.length === 1) {
            onGroupCardClick(documentTemplateGroupList[0])
        }
    }, [verificationId, documentTemplateGroupList, onGroupCardClick]);

    const generateMyMahiActionCard = () => {
        return (
            <div className={`width-full pick-document-card`}>
                <ActionCardComponent icon=<MyMahiIcon/>
                                     text='MyMahi Digital Learner ID'
                                     arrow
                                     noBackground
                                     onCardClick={() => navigateToMyMahiVerification(verificationId)}/>
            </div>
        )
    }

    const navigateToMyMahiVerification = async (verificationId: string | undefined) => {
        if (verificationId) {
            const redirectUrl = await requestVerification(verificationId)
            window.open(redirectUrl, '_blank')
            window.__appNavigate?.(`/${verificationId}/mymahi`)
        }
    }

    const selectIconByDocumentTemplate = (name: string) => {
        if (name.includes('International')) {
            return icons.intPassport
        } else if (name.includes('passport')) {
            return icons.idVertical
        } else {
            return icons.idHorizontal
        }
    }

    const selectIconByDocumentTemplateGroup = (name: string) => {
        if (name.includes('licence')) {
            return <VerifiedIcon/>
        } else if (name.includes('Student')) {
            return icons.idHorizontal
        } else {
            return <NoIDIcon/>
        }
    }

    const createDocumentTemplateGroupComponent = () => {
        return (
            <div className={`background background-white pick-document-container general-container `}>
                <HeaderComponent
                    title="Let's verify your ID"
                    subtitle="Choose the ID you’ve got handy so we can confirm a few details. It only takes a couple of minutes."
                    logo
                    progress={1}
                />
                {
                    documentTemplateGroupList.map(group => (
                        <div className={`width-full pick-document-card`}>
                            <ActionCardComponent icon={selectIconByDocumentTemplateGroup(group.name)} text={group.name} arrow
                                                 subtext={group.description}
                                                 fontColor={group.name.includes('licence') ? '#0CA288' : ''}
                                                 gradient={group.name.includes('licence')}
                                                 secondaryGradient={group.name.includes('Student')}
                                                 onCardClick={() => {
                                                     onGroupCardClick(group)
                                                 }}/>
                        </div>
                    ))
                }

            </div>
        )

    }

    const createDocumentTemplateComponent = () => {
        if (!documentTemplateLoading && documentTemplateList.length > 1) {
            return (
                <div className={`background background-white pick-document-container general-container `}>
                    <HeaderComponent
                        title={templateHeader}
                        subtitle={templateSubheader}
                        progress={5}
                        back
                        onBackClick={() => setGroupChoiceMade(false)}
                    />
                    {
                        documentTemplateList.map(template => {
                            if (template.name.includes('MyMahi')) {
                                return generateMyMahiActionCard()
                            }
                            return (
                                <div className={`width-full pick-document-card`}>
                                    <ActionCardComponent icon={selectIconByDocumentTemplate(template.name)} text={template.name} arrow
                                                         subtext={template.description}
                                                         gradient={!template.name.includes('student')}
                                                         secondaryGradient={template.name.includes('student')}
                                                         onCardClick={() => {
                                                             onTemplateCardClick(template.id)
                                                         }}/>
                                </div>
                            )})
                    }
                </div>
            )
        }
    }

    if (documentTemplateGroupLoading || documentTemplateLoading || documentTemplateList.length === 1 || myMahiLoading) {
        return <ProcessingComponent loadingAnimation="processing" success={false} successAnimation="processingSuccess" noText/>
    }

    return groupChoiceMade ? createDocumentTemplateComponent(): createDocumentTemplateGroupComponent()
}